import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  SolarQuoteStepOrder,
  SolarQuoteSteps,
} from '@/constants/solarQuoteEcomm';
import type { MarketInfo } from '@/types/models/solarEcomm/marketInfo';

export type SolarQuoteState = {
  selectedBatteryOptionIndex: number | undefined;
  selectedPanelConfigIndex: number | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  zipCode: number | undefined;
  ownsHome: boolean;
  marketInfo: MarketInfo | undefined;
  ecommReferrerPage: string | undefined;

  address: { street: string; state: string; zipCode: number } | undefined;
  coordinates: { lat: number; long: number } | undefined;
  currentStep: SolarQuoteSteps;

  // Ecomm intake
  avgMonthlyBill: number | undefined;
  availableUtilities: Array<string>;
  selectedUtility: string;
  state: string | undefined;
};

export const initialState: SolarQuoteState = {
  selectedPanelConfigIndex: undefined,
  selectedBatteryOptionIndex: undefined,
  firstName: undefined,
  lastName: undefined,
  phone: undefined,
  email: undefined,
  ownsHome: false,
  currentStep: SolarQuoteSteps.LEAD,
  marketInfo: undefined,
  ecommReferrerPage: undefined,

  address: undefined,
  coordinates: undefined,

  // Ecomm intake
  zipCode: undefined,
  avgMonthlyBill: undefined,
  availableUtilities: [],
  selectedUtility: '',
  state: undefined,
};

type ContactActionType = NonNullable<
  Pick<
    SolarQuoteState,
    'firstName' | 'lastName' | 'phone' | 'email' | 'ownsHome'
  >
>;

export const solarQuoteSlice = createSlice({
  name: 'SolarQuote',
  initialState,
  reducers: {
    incrementStep: (state) => {
      const newIndex = SolarQuoteStepOrder.indexOf(state.currentStep) + 1;
      const maxLength = SolarQuoteStepOrder.length;
      if (newIndex <= maxLength) {
        state.currentStep = SolarQuoteStepOrder[newIndex];
      } else {
        state.currentStep = SolarQuoteStepOrder[maxLength];
      }
    },
    decrementStep: (state) => {
      const newIndex = SolarQuoteStepOrder.indexOf(state.currentStep) - 1;
      if (newIndex >= 0) {
        state.currentStep = SolarQuoteStepOrder[newIndex];
      } else {
        state.currentStep = SolarQuoteStepOrder[0];
      }
    },
    setContactInformation: (
      state,
      action: PayloadAction<ContactActionType>
    ) => {
      const { firstName, lastName, phone, email } = action.payload;
      state.firstName = firstName;
      state.lastName = lastName;
      state.phone = phone;
      state.email = email;
    },
    setCoordinates: (
      state,
      action: PayloadAction<{ lat: number; long: number }>
    ) => {
      const { lat, long } = action.payload;
      state.coordinates = { lat, long };
    },
    setAddress: (
      state,
      action: PayloadAction<{ street: string; state: string; zipCode: number }>
    ) => {
      const { state: addressState, zipCode } = action.payload;
      state.address = action.payload;
      state.state = addressState;
      state.zipCode = zipCode;
    },
    setUtility: (state, action: PayloadAction<{ utility: string }>) => {
      const { utility } = action.payload;
      state.selectedUtility = utility;
    },
    setSelectedBatteryOptionIndex: (
      state,
      action: PayloadAction<{ batteryOptionIndex: number }>
    ) => {
      const { batteryOptionIndex } = action.payload;
      state.selectedBatteryOptionIndex = batteryOptionIndex;
    },
    setSelectedPanelConfigIndex: (
      state,
      action: PayloadAction<{ panelConfigIndex: number }>
    ) => {
      const { panelConfigIndex } = action.payload;
      state.selectedPanelConfigIndex = panelConfigIndex;
    },

    // TODO: PXE-2698 Ecomm intake
    ecommReferrerPage: (_state) => {},
    setMarketInfo: (_state) => {},
    setAvgMonthlyBill: (_state) => {},
    setAvailableUtils: (_state) => {},
    resetSolarQuote: () => {
      return initialState;
    },
  },
  selectors: {
    selectPanelConfigIndex: (sliceState) => sliceState.selectedPanelConfigIndex,
    selectBatteryOptionIndex: (sliceState) =>
      sliceState.selectedBatteryOptionIndex,
    selectBasicUserInfo: createSelector(
      (sliceState: SolarQuoteState) => sliceState?.address?.zipCode,
      (sliceState: SolarQuoteState) => sliceState?.address?.state,
      (sliceState: SolarQuoteState) => sliceState.selectedUtility,
      (sliceState: SolarQuoteState) => sliceState.coordinates,
      (
        zip: number | undefined,
        state: string | undefined,
        utility: string | undefined,
        coordinates: { lat: number; long: number } | undefined
      ) => {
        if (!zip || !state || !utility || !coordinates) {
          return undefined;
        }

        return { zip, state, utility, ...coordinates };
      }
    ),
  },
});

export const {
  incrementStep,
  decrementStep,
  setContactInformation,
  setCoordinates,
  setAddress,
  setUtility,
  setSelectedBatteryOptionIndex,
  setSelectedPanelConfigIndex,
} = solarQuoteSlice.actions;

export const {
  selectPanelConfigIndex,
  selectBatteryOptionIndex,
  selectBasicUserInfo,
} = solarQuoteSlice.selectors;

export default solarQuoteSlice.reducer;
export type SolarQuoteSlice = typeof solarQuoteSlice;
